import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowPathIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import AuthLayout, { AuthLabel, AuthError, AuthLink } from '../components/ui/AuthLayout';
import PasswordInput, { generateSecurePassword, passwordMeetsRules } from '../components/ui/PasswordInput';
import { Button } from '../components/ui/button';
import { AuthSkeleton } from '../components/ui/Skeleton';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const { updatePassword, logout } = useAuth();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Check if recovery session or token is active in Supabase
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      // Supabase parses hash fragment (#access_token=...&type=recovery) automatically
      const hash = window.location.hash;
      const isRecovery = hash.includes('type=recovery') || !!session;
      setReady(true);
      if (!isRecovery && !session) {
        setError('Lidhja e rivendosjes mungon ose ka skaduar. Ju lutem kërkoni një lidhje të re.');
      }
    };

    checkSession();
  }, []);

  const passwordsMatch = !confirmPassword || password === confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!passwordMeetsRules(password)) {
      setError('Fjalëkalimi nuk i plotëson kërkesat.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Fjalëkalimet nuk përputhen.');
      return;
    }

    setLoading(true);
    setError('');

    const res = await updatePassword(password);
    setLoading(false);

    if (res.success) {
      setSuccess(true);
      setTimeout(async () => {
        await logout();
        navigate('/login');
      }, 2500);
    } else {
      setError(res.error || 'Dështoi përditësimi i fjalëkalimit. Lidhja mund të ketë skaduar.');
    }
  };

  if (!ready) {
    return <AuthSkeleton />;
  }

  return (
    <AuthLayout
      title="Vendos fjalëkalimin e ri"
      subtitle="Zgjidhni një fjalëkalim të sigurt për llogarinë tuaj."
      footer={
        <>
          Kujtuat fjalëkalimin? <AuthLink to="/login">Kthehu te hyrja</AuthLink>
        </>
      }
    >
      {success ? (
        <div className="space-y-5">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/80 p-4 text-sm text-emerald-900 flex items-start gap-3">
            <CheckCircleIcon className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold mb-1">Fjalëkalimi u ndryshua me sukses!</p>
              <p className="text-emerald-700 text-xs leading-relaxed">
                Po ju ridrejtojmë te faqja e hyrjes...
              </p>
            </div>
          </div>
          <Button asChild variant="outline" className="w-full h-10 border-zinc-200 hover:bg-zinc-100 text-zinc-900 font-medium">
            <Link to="/login">Shko te hyrja menjëherë</Link>
          </Button>
        </div>
      ) : (
        <form className="space-y-5" onSubmit={handleSubmit}>
          {error && <AuthError>{error}</AuthError>}

          {/* New Password */}
          <div className="space-y-1.5">
            <AuthLabel htmlFor="password">Fjalëkalimi i ri</AuthLabel>
            <PasswordInput
              id="password"
              name="password"
              required
              value={password}
              onChange={setPassword}
              onGenerate={() => {
                const generated = generateSecurePassword();
                setPassword(generated);
                setConfirmPassword(generated);
                setShowConfirm(true);
              }}
              showStrength
              placeholder="••••••••"
              autoComplete="new-password"
            />
          </div>

          {/* Confirm Password */}
          <div className="space-y-1.5">
            <AuthLabel htmlFor="confirmPassword">Konfirmo fjalëkalimin</AuthLabel>
            <PasswordInput
              id="confirmPassword"
              name="confirmPassword"
              required
              value={confirmPassword}
              onChange={setConfirmPassword}
              visible={showConfirm}
              onVisibleChange={setShowConfirm}
              invalid={!passwordsMatch}
              placeholder="••••••••"
              autoComplete="new-password"
            />
            {!passwordsMatch && (
              <p className="text-xs font-medium text-red-600 mt-1">Fjalëkalimet nuk përputhen</p>
            )}
          </div>

          <Button
            type="submit"
            disabled={loading || !passwordsMatch || !passwordMeetsRules(password)}
            className="w-full h-11 bg-black hover:bg-zinc-800 text-white font-bold rounded-xl text-sm transition-colors"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
                Duke ruajtur...
              </span>
            ) : (
              'Ruaj fjalëkalimin e ri'
            )}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
};

export default ResetPasswordPage;
